import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, CalendarCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumb, CtaBand } from "@/components/PageBits";
import { useLead } from "@/context/LeadContext";
import { useBooking } from "@/context/BookingContext";
import { AnimatedLines } from "@/components/landing/AnimatedLines";
import { findCategory, servicesOfCategory } from "@/data/pages";

export default function CategoryPage() {
  const { slug } = useParams();
  const { openForm } = useLead();
  const { openBooking } = useBooking();
  const category = findCategory(slug);
  if (!category) return <Navigate to="/404" replace />;
  const services = servicesOfCategory(slug);

  return (
    <div data-testid="category-page">
      <Seo title={category.metaTitle} description={category.metaDescription} path={`/${category.slug}`} image={category.image} />

      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute inset-0">
          <img src={category.image} alt={category.name} className="w-full h-full object-cover opacity-30" />
        </div>
        <AnimatedLines variant="dark" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-16 lg:py-24">
          <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: category.name }]} />
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05] max-w-3xl">
            {category.name}
          </h1>
          <p className="text-white/80 mt-5 text-base sm:text-lg leading-relaxed max-w-2xl">{category.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {!category.noBooking && (
              <button
                data-testid="category-book"
                onClick={openBooking}
                className="cta inline-flex items-center gap-2 bg-royal text-white font-extrabold px-7 py-4 rounded-sm hover:bg-royal-hover shadow-lg shadow-royal/30"
              >
                <CalendarCheck className="w-5 h-5" /> Réserver mon créneau
              </button>
            )}
            <button
              data-testid="category-cta"
              onClick={() => openForm(category.service)}
              className={`cta inline-flex items-center gap-2 font-bold px-7 py-4 rounded-sm ${
                category.noBooking
                  ? "bg-royal text-white hover:bg-royal-hover shadow-lg shadow-royal/30"
                  : "border border-white/40 text-white hover:bg-white hover:text-ink"
              }`}
            >
              Obtenir mon devis <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight text-ink mb-8">Nos prestations</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="lift group flex flex-col bg-white border border-border rounded-sm overflow-hidden hover:border-ink"
            >
              <div className="h-44 overflow-hidden">
                <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-heading font-bold text-lg text-ink">{s.name}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{s.intro.slice(0, 110)}…</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-ink">
                  Voir la prestation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CtaBand onReserve={() => openForm(category.service)} onBook={category.noBooking ? undefined : openBooking} title="Besoin d'un devis personnalisé ?" subtitle="Réponse rapide, intervention 24/48h." />
    </div>
  );
}
